import { Task, AnalysisResult, Recommendation } from '../types';
import { parseTasks } from '../utils/markdown';

export interface TaskAnalyzerOptions {
  enableAI?: boolean;
  apiKey?: string;
}

export class TaskAnalyzer {
  private enableAI: boolean;
  private apiKey?: string;

  constructor(options: TaskAnalyzerOptions = {}) {
    this.enableAI = options.enableAI || false;
    this.apiKey = options.apiKey;
  }

  async analyze(content: string): Promise<AnalysisResult> {
    const tasks = parseTasks(content);
    
    // Basic statistics
    const statistics = this.calculateStatistics(tasks);
    
    // Generate insights
    const insights = this.generateInsights(tasks);
    
    // Generate recommendations
    const recommendations = await this.generateRecommendations(tasks, insights);
    
    return {
      tasks,
      insights,
      recommendations,
      statistics
    };
  }

  private calculateStatistics(tasks: Task[]) {
    const total = tasks.length;
    const completed = tasks.filter(t => t.status === 'completed').length;
    const inProgress = tasks.filter(t => t.status === 'in_progress').length;
    const pending = tasks.filter(t => t.status === 'pending').length;
    const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;
    
    return {
      total,
      completed,
      inProgress,
      pending,
      completionRate
    };
  }

  private generateInsights(tasks: Task[]) {
    const trends: string[] = [];
    const bottlenecks: string[] = [];
    const suggestions: string[] = [];
    
    // Analyze trends
    const highPriorityCount = tasks.filter(t => t.priority === 'high' && t.status !== 'completed').length;
    if (highPriorityCount > 5) {
      trends.push(`High number of incomplete high-priority tasks (${highPriorityCount})`);
    }
    
    // Check for stale in-progress tasks
    const inProgressCount = tasks.filter(t => t.status === 'in_progress').length;
    if (inProgressCount > 3) {
      bottlenecks.push(`${inProgressCount} tasks in progress - consider completing some before starting new ones`);
    }
    
    // Analyze task distribution
    const sections = new Set(tasks.map(t => t.section).filter(Boolean));
    if (sections.size > 1) {
      const sectionCounts = Array.from(sections).map(section => ({
        section,
        count: tasks.filter(t => t.section === section).length
      }));
      
      const maxSection = sectionCounts.reduce((a, b) => a.count > b.count ? a : b);
      trends.push(`Most tasks in "${maxSection.section}" section (${maxSection.count} tasks)`);
    }
    
    // Generate suggestions based on patterns
    if (tasks.length > 20 && tasks.filter(t => t.status === 'completed').length < 5) {
      suggestions.push('Consider breaking down large tasks into smaller, more manageable subtasks');
    }
    
    const testTasks = tasks.filter(t => t.content.toLowerCase().includes('test'));
    if (testTasks.length > 0 && testTasks.every(t => t.status !== 'completed')) {
      suggestions.push('Prioritize completing test-related tasks to ensure code quality');
    }
    
    return { trends, bottlenecks, suggestions };
  }

  private async generateRecommendations(
    tasks: Task[],
    insights: { trends: string[]; bottlenecks: string[]; suggestions: string[] }
  ): Promise<Recommendation[]> {
    const recommendations: Recommendation[] = [];
    
    // High priority incomplete tasks
    const urgentTasks = tasks.filter(t => t.priority === 'high' && t.status === 'pending');
    if (urgentTasks.length > 0) {
      recommendations.push({
        type: 'task',
        priority: 'high',
        title: 'Complete high-priority tasks',
        description: `You have ${urgentTasks.length} high-priority tasks pending. Focus on these first.`,
        action: urgentTasks[0].content
      });
    }
    
    // Too many in-progress tasks
    const inProgressTasks = tasks.filter(t => t.status === 'in_progress');
    if (inProgressTasks.length > 3) {
      recommendations.push({
        type: 'workflow',
        priority: 'medium',
        title: 'Reduce work in progress',
        description: 'Too many tasks in progress can reduce productivity. Complete current tasks before starting new ones.',
        action: 'Review and complete in-progress tasks'
      });
    }
    
    // Time-based recommendations
    const hour = new Date().getHours();
    if (hour < 12) {
      recommendations.push({
        type: 'improvement',
        priority: 'low',
        title: 'Morning productivity tip',
        description: 'Start with high-priority or challenging tasks while your energy is high',
        action: 'Focus on complex tasks'
      });
    } else if (hour > 16) {
      recommendations.push({
        type: 'improvement',
        priority: 'low',
        title: 'End-of-day recommendation',
        description: 'Good time for code reviews, documentation, and planning tomorrow\'s tasks',
        action: 'Review and document today\'s work'
      });
    }
    
    // AI-powered recommendations (if enabled)
    if (this.enableAI && this.apiKey) {
      // In a real implementation, this would call an AI API
      // For now, we'll add a placeholder
      recommendations.push({
        type: 'improvement',
        priority: 'medium',
        title: 'AI Analysis Available',
        description: 'Enable AI features for smarter task prioritization and insights',
        action: 'Configure AI settings'
      });
    }
    
    return recommendations;
  }

  async suggestNextTask(tasks: Task[]): Promise<Task | null> {
    // Filter for actionable tasks
    const actionableTasks = tasks.filter(t => 
      t.status === 'pending' || t.status === 'in_progress'
    );
    
    if (actionableTasks.length === 0) {
      return null;
    }
    
    // Prioritize in-progress tasks
    const inProgressTasks = actionableTasks.filter(t => t.status === 'in_progress');
    if (inProgressTasks.length > 0) {
      // Return highest priority in-progress task
      return inProgressTasks.sort((a, b) => {
        const priorityOrder = { high: 0, medium: 1, low: 2 };
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      })[0];
    }
    
    // Otherwise, suggest highest priority pending task
    const pendingTasks = actionableTasks.filter(t => t.status === 'pending');
    return pendingTasks.sort((a, b) => {
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    })[0];
  }
}